import { StargraveActiveBuff, StargraveBuff, StargraveStat, StargraveStatLine } from '../types';
import { getAllStargraveBuffs } from '../stores/stargraveCustomStore';
import { StatModifier } from '../utils/splitStats';

/** Merged static + custom lookup — custom buffs are game-scoped to Stargrave. */
export const getStargraveBuffById = (buffId: string): StargraveBuff | undefined =>
  getAllStargraveBuffs().find((buff) => buff.id === buffId);

/** Buff granted by resolving a power pre-game (Armoury's weapon boost is handled separately). */
export const getStargraveBuffForPower = (powerId: string): StargraveBuff | undefined =>
  powerId === 'armoury'
    ? undefined
    : getAllStargraveBuffs().find((buff) => buff.source === 'power' && buff.sourceId === powerId);

export function formatStargraveBuffEffect(buff: StargraveBuff): string {
  if (buff.displayEffect) return buff.displayEffect;
  const parts = (Object.entries(buff.statModifiers ?? {}) as [string, number][])
    .map(([stat, value]) => `${value >= 0 ? '+' : ''}${value} ${stat[0].toUpperCase()}${stat.slice(1)}`);
  return parts.length > 0 ? parts.join(', ') : 'No stat changes';
}

export function addStargraveBuff(activeBuffs: StargraveActiveBuff[], buffId: string): StargraveActiveBuff[] {
  const buff = getStargraveBuffById(buffId);
  if (!buff) throw new Error('Unknown buff.');
  const withoutExclusive = buff.exclusiveGroup
    ? activeBuffs.filter((active) => getStargraveBuffById(active.buffId)?.exclusiveGroup !== buff.exclusiveGroup || active.buffId === buffId)
    : activeBuffs;
  const existing = withoutExclusive.find((active) => active.buffId === buffId);
  if (!existing) return [...withoutExclusive, { buffId, stackCount: 1 }];
  const maxStacks = buff.stacking ?? 1;
  if (existing.stackCount >= maxStacks) throw new Error(`${buff.name} is already at maximum stacks.`);
  return withoutExclusive.map((active) => active.buffId === buffId ? { ...active, stackCount: active.stackCount + 1 } : active);
}

export function removeStargraveBuff(
  activeBuffs: StargraveActiveBuff[],
  buffId: string,
  count: number | 'all' = 'all'
): StargraveActiveBuff[] {
  if (count === 'all') return activeBuffs.filter((active) => active.buffId !== buffId);
  return activeBuffs.flatMap((active) => {
    if (active.buffId !== buffId) return [active];
    const remaining = active.stackCount - count;
    return remaining > 0 ? [{ ...active, stackCount: remaining }] : [];
  });
}

export function getStargraveBuffStatModifiers(
  activeBuffs: StargraveActiveBuff[],
  stat: StargraveStat
): StatModifier[] {
  return activeBuffs.flatMap((active) => {
    const buff = getStargraveBuffById(active.buffId);
    const value = buff?.statModifiers?.[stat];
    if (!buff || value === undefined) return [];
    return [{ label: active.stackCount > 1 ? `${buff.name} ×${active.stackCount}` : buff.name, value: value * active.stackCount, type: 'add' as const }];
  });
}

export function applyStargraveBuffsToStats(
  baseStats: StargraveStatLine,
  activeBuffs: StargraveActiveBuff[]
): StargraveStatLine {
  const stats = { ...baseStats };
  for (const stat of ['move', 'fight', 'shoot', 'armour', 'will', 'health'] as StargraveStat[]) {
    const contributions = activeBuffs.flatMap((active) => {
      const buff = getStargraveBuffById(active.buffId);
      const value = buff?.statModifiers?.[stat];
      if (!buff || value === undefined) return [];
      return [{ delta: value * active.stackCount, cap: buff.statMaximums?.[stat] }];
    });
    let value = stats[stat];
    // Uncapped bonuses apply in full; each capped buff only limits its own gain
    // and never reduces value earned elsewhere.
    for (const { delta, cap } of contributions) {
      if (cap === undefined) value += delta;
    }
    for (const { delta, cap } of contributions) {
      if (cap !== undefined) value = Math.min(value + delta, Math.max(value, cap));
    }
    stats[stat] = value;
  }
  return stats;
}
